import { useState, useEffect } from 'react';
import { authFetch } from '../utils/api';

const C = {
  navy:       '#0C1A36',
  navy2:      '#112240',
  blue:       '#1A7FBE',
  teal:       '#00A8CC',
  green:      '#3DAA4A',
  gold:       '#F5C800',
  offwhite:   '#EDF1FF',
  muted:      '#7A90B8',
  red:        '#C0223A',
};

const card = {
  background:   C.navy2,
  borderRadius: '10px',
  borderTop:    `3px solid ${C.teal}`,
  padding:      '24px 28px',
  marginBottom: '20px',
};

const cardTitle = {
  color:        C.gold,
  fontWeight:   '700',
  fontSize:     '1rem',
  marginBottom: '18px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const rowStyle = {
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'space-between',
  padding:        '10px 0',
  borderBottom:   `1px solid rgba(122,144,184,0.15)`,
};

const labelStyle = {
  color:    C.muted,
  fontSize: '0.875rem',
  minWidth: '120px',
};

const valueStyle = {
  color:    C.offwhite,
  fontSize: '0.95rem',
  flex:     1,
};

const inputStyle = {
  background:   'rgba(255,255,255,0.07)',
  border:       `1px solid ${C.teal}`,
  borderRadius: '6px',
  color:        C.offwhite,
  fontSize:     '0.95rem',
  fontFamily:   'Sora, sans-serif',
  padding:      '6px 10px',
  outline:      'none',
  width:        '100%',
};

const btnPrimary = {
  background:   C.teal,
  color:        '#fff',
  border:       'none',
  borderRadius: '6px',
  padding:      '8px 18px',
  fontSize:     '0.875rem',
  fontFamily:   'Sora, sans-serif',
  fontWeight:   '600',
  cursor:       'pointer',
};

const btnGhost = {
  background:   'transparent',
  color:        C.muted,
  border:       `1px solid ${C.muted}`,
  borderRadius: '6px',
  padding:      '7px 16px',
  fontSize:     '0.875rem',
  fontFamily:   'Sora, sans-serif',
  cursor:       'pointer',
  marginLeft:   '8px',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function initials(user) {
  if (!user) return '?';
  const src = user.name || user.email || '';
  return src.charAt(0).toUpperCase() || '?';
}

function planStatus(user) {
  const step = user.onboarding_step || '';
  if (step === 'active')    return 'active';
  if (step === 'suspended') return 'suspended';
  return 'trial';
}

function StatusBadge({ status }) {
  const map = {
    active:    { label: 'Active',    color: C.green },
    trial:     { label: 'Trial',     color: C.gold  },
    suspended: { label: 'Suspended', color: C.red   },
  };
  const { label, color } = map[status] || map.trial;
  return (
    <span style={{
      background:   color + '22',
      border:       `1px solid ${color}`,
      color,
      borderRadius: '20px',
      padding:      '2px 12px',
      fontSize:     '0.8rem',
      fontWeight:   '600',
    }}>
      {label}
    </span>
  );
}

export default function Profile() {
  const [user,          setUser]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [fetchError,    setFetchError]    = useState('');
  const [editingName,   setEditingName]   = useState(false);
  const [nameInput,     setNameInput]     = useState('');
  const [nameError,     setNameError]     = useState('');
  const [billingLoading, setBillingLoading] = useState(false);
  const [passwordForm,  setPasswordForm]  = useState({ current: '', newPass: '', confirm: '' });
  const [passwordMsg,   setPasswordMsg]   = useState({ text: '', type: '' });

  useEffect(() => {
    authFetch('/api/users/me')
      .then(r => r.json())
      .then(data => {
        setUser(data.user);
        setNameInput(data.user.name || '');
      })
      .catch(err => setFetchError(err.message))
      .finally(() => setLoading(false));
  }, []);

  async function saveName() {
    setNameError('');
    try {
      const res  = await authFetch('/api/users/profile', {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name: nameInput }),
      });
      const data = await res.json();
      if (!res.ok) {
        setNameError(data.error || 'Failed to save');
        return;
      }
      setUser(u => ({ ...u, name: data.name }));
      setEditingName(false);
    } catch {
      setNameError('Failed to save');
    }
  }

  async function openPortal() {
    setBillingLoading(true);
    try {
      const res  = await authFetch('/api/stripe/portal', { method: 'POST' });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Billing portal unavailable'); return; }
      window.location.href = data.url;
    } catch {
      alert('Could not open billing portal');
    } finally {
      setBillingLoading(false);
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    setPasswordMsg({ text: '', type: '' });
    const { current, newPass, confirm } = passwordForm;

    if (newPass !== confirm) {
      setPasswordMsg({ text: 'New passwords do not match', type: 'error' });
      return;
    }

    try {
      const res  = await authFetch('/api/users/change-password', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ currentPassword: current, newPassword: newPass }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPasswordMsg({ text: data.error || 'Failed to update password', type: 'error' });
        return;
      }
      setPasswordMsg({ text: 'Password updated successfully', type: 'success' });
      setPasswordForm({ current: '', newPass: '', confirm: '' });
    } catch {
      setPasswordMsg({ text: 'Failed to update password', type: 'error' });
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
      <div style={{
        width: '40px', height: '40px',
        border: `4px solid ${C.navy2}`,
        borderTop: `4px solid ${C.teal}`,
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (fetchError) return (
    <div style={{ textAlign: 'center', color: C.red, padding: '60px 20px', fontFamily: 'Sora, sans-serif' }}>
      Failed to load profile: {fetchError}
    </div>
  );

  const status   = planStatus(user);
  const isActive = user.plan === 'active' || user.onboarding_step === 'active';

  return (
    <div style={{
      background:  C.navy,
      minHeight:   '100vh',
      padding:     '40px 16px 80px',
      fontFamily:  'Sora, sans-serif',
      color:       C.offwhite,
    }}>
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>

        {/* ── Section 1: Profile Header ── */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{
            width:          '88px',
            height:         '88px',
            borderRadius:   '50%',
            background:     C.navy2,
            border:         `3px solid ${C.teal}`,
            display:        'inline-flex',
            alignItems:     'center',
            justifyContent: 'center',
            fontSize:       '2.2rem',
            fontWeight:     '700',
            color:          C.teal,
            marginBottom:   '14px',
          }}>
            {initials(user)}
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: '700', color: C.offwhite }}>
            {user.name || user.email}
          </div>
          <div style={{ color: C.muted, fontSize: '0.9rem', margin: '4px 0 10px' }}>
            {user.email}
          </div>
          <span style={{
            background:   user.google_id ? C.teal + '22' : C.blue + '22',
            border:       `1px solid ${user.google_id ? C.teal : C.blue}`,
            color:        user.google_id ? C.teal : C.blue,
            borderRadius: '20px',
            padding:      '3px 14px',
            fontSize:     '0.8rem',
            fontWeight:   '600',
          }}>
            {user.google_id ? 'Google Account' : 'Email Account'}
          </span>
        </div>

        {/* ── Section 2: Account Details ── */}
        <div style={card}>
          <div style={cardTitle}>Account Details</div>

          <div style={rowStyle}>
            <span style={labelStyle}>Name</span>
            {editingName ? (
              <div style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  style={inputStyle}
                  value={nameInput}
                  maxLength={80}
                  onChange={e => setNameInput(e.target.value)}
                  autoFocus
                />
                <button style={btnPrimary} onClick={saveName}>Save</button>
                <button style={btnGhost} onClick={() => { setEditingName(false); setNameError(''); setNameInput(user.name || ''); }}>
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={valueStyle}>{user.name || <span style={{ color: C.muted }}>Not set</span>}</span>
                <button
                  onClick={() => setEditingName(true)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.teal, padding: '4px', lineHeight: 1 }}
                  title="Edit name"
                >
                  ✏️
                </button>
              </div>
            )}
          </div>
          {nameError && <div style={{ color: C.red, fontSize: '0.8rem', marginTop: '4px' }}>{nameError}</div>}

          <div style={{ ...rowStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>Member since</span>
            <span style={valueStyle}>{formatDate(user.created_at)}</span>
          </div>
        </div>

        {/* ── Section 3: Subscription ── */}
        <div style={card}>
          <div style={cardTitle}>Subscription</div>

          <div style={rowStyle}>
            <span style={labelStyle}>Plan</span>
            <span style={valueStyle}>Acct4Cast Pro</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Price</span>
            <span style={valueStyle}>$49 / month</span>
          </div>
          <div style={{ ...rowStyle, borderBottom: 'none' }}>
            <span style={labelStyle}>Status</span>
            <StatusBadge status={status} />
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {(() => {
              const showManageBilling = isActive && !!user.stripe_customer_id;
              const showActivate      = !isActive;
              if (showManageBilling) return (
                <button
                  onClick={openPortal}
                  disabled={billingLoading}
                  style={{
                    background:   `linear-gradient(135deg, ${C.teal}, ${C.green})`,
                    color:        '#fff',
                    border:       'none',
                    borderRadius: '6px',
                    padding:      '10px 22px',
                    fontSize:     '0.9rem',
                    fontFamily:   'Sora, sans-serif',
                    fontWeight:   '600',
                    cursor:       billingLoading ? 'not-allowed' : 'pointer',
                    opacity:      billingLoading ? 0.7 : 1,
                  }}
                >
                  {billingLoading ? 'Opening…' : 'Manage Account'}
                </button>
              );
              if (showActivate) return (
                <button
                  onClick={() => { window.location.href = '/onboarding'; }}
                  style={{
                    background:   C.gold,
                    color:        C.navy,
                    border:       'none',
                    borderRadius: '6px',
                    padding:      '10px 22px',
                    fontSize:     '0.9rem',
                    fontFamily:   'Sora, sans-serif',
                    fontWeight:   '700',
                    cursor:       'pointer',
                  }}
                >
                  Activate Subscription
                </button>
              );
              return null;
            })()}
          </div>
        </div>

        {/* ── Section 4: Security (email users only) ── */}
        {!user.google_id && (
          <div style={card}>
            <div style={cardTitle}>Security</div>
            <form onSubmit={changePassword}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ ...labelStyle, display: 'block', marginBottom: '6px' }}>Current password</label>
                <input
                  type="password"
                  style={inputStyle}
                  value={passwordForm.current}
                  onChange={e => setPasswordForm(f => ({ ...f, current: e.target.value }))}
                  required
                />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ ...labelStyle, display: 'block', marginBottom: '6px' }}>New password</label>
                <input
                  type="password"
                  style={inputStyle}
                  value={passwordForm.newPass}
                  onChange={e => setPasswordForm(f => ({ ...f, newPass: e.target.value }))}
                  required
                />
              </div>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ ...labelStyle, display: 'block', marginBottom: '6px' }}>Confirm new password</label>
                <input
                  type="password"
                  style={inputStyle}
                  value={passwordForm.confirm}
                  onChange={e => setPasswordForm(f => ({ ...f, confirm: e.target.value }))}
                  required
                />
              </div>
              <button type="submit" style={btnPrimary}>Update Password</button>
              {passwordMsg.text && (
                <div style={{
                  marginTop: '12px',
                  color:     passwordMsg.type === 'success' ? C.green : C.red,
                  fontSize:  '0.875rem',
                }}>
                  {passwordMsg.text}
                </div>
              )}
            </form>
          </div>
        )}

      </div>
    </div>
  );
}
